/**
 * D3.js force-directed graph visualization
 */
import * as d3 from "d3";

export class GraphRenderer {
  constructor(svgElement) {
    this.svg = d3.select(svgElement);
    this.width = svgElement.clientWidth;
    this.height = svgElement.clientHeight;
    this.simulation = null;
    this.nodes = [];
    this.edges = [];
    this.onNodeClick = null;
    this.onNodeDrillDown = null; // Callback for double-click/zoom into children
    this.showEdgeEmojis = false; // Toggle for edge emojis
    this.spacingMultiplier = 1.0; // Spacing control (0.5 = tight, 2.0 = spread out)
    this.selectedNodeId = null; // Track currently selected node

    // Relationship styling
    this.relationshipColors = {
      causes: "#E74C3C", // Red - causal
      supports: "#2ECC71", // Green - positive
      contrasts: "#F39C12", // Orange - conflict
      leads_to: "#3498DB", // Blue - sequential
      part_of: "#9B59B6", // Purple - hierarchical
      related_to: "#95A5A6", // Gray - general
    };

    this.relationshipEmojis = {
      causes: "➡️",
      supports: "🤝",
      contrasts: "⚡",
      leads_to: "→",
      part_of: "📦",
      related_to: "🔗",
    };

    this.initSVG();
    this.setupZoom();
  }

  initSVG() {
    this.svg.selectAll("*").remove();

    // Create main group for zoom/pan
    this.mainGroup = this.svg.append("g").attr("class", "main-group");

    // Create groups for links and nodes
    this.linkGroup = this.mainGroup.append("g").attr("class", "links");
    this.nodeGroup = this.mainGroup.append("g").attr("class", "nodes");
  }

  setupZoom() {
    const zoom = d3
      .zoom()
      .scaleExtent([0.1, 4])
      .on("zoom", (event) => {
        this.mainGroup.attr("transform", event.transform);
      });

    this.svg.call(zoom);
  }

  render(graphData) {
    this.nodes = graphData.nodes;
    this.edges = graphData.edges;

    // Clear selection when clicking on SVG background
    this.svg.on("click", () => {
      this.setSelectedNode(null);
      // Also hide node details panel if callback exists
      if (this.onNodeClick) {
        this.onNodeClick(null);
      }
    });

    // Create force simulation
    this.simulation = d3
      .forceSimulation(this.nodes)
      .force(
        "link",
        d3
          .forceLink(this.edges)
          .id((d) => d.id)
          .distance((d) => {
            const baseDistance = 150 / (d.strength || 5);
            // For high-strength edges (invisible edges), apply squared multiplier for more dramatic spacing
            const multiplier = (d.strength || 5) >= 7
              ? Math.pow(this.spacingMultiplier, 2)
              : this.spacingMultiplier;
            return baseDistance * multiplier;
          })
          .strength((d) => {
            const baseStrength = (d.strength || 5) / 10;
            // For high-strength edges, divide by squared multiplier to weaken them more
            const divisor = (d.strength || 5) >= 7
              ? Math.pow(this.spacingMultiplier, 2)
              : this.spacingMultiplier;
            return baseStrength / divisor;
          }),
      )
      .force("charge", d3.forceManyBody().strength(-200 * this.spacingMultiplier)) // Apply spacing to repulsion
      .force("center", d3.forceCenter(this.width / 2, this.height / 2).strength(0.3)) // Increased from 0.1 to pull nodes to center
      .force(
        "collision",
        d3.forceCollide().radius((d) => ((d.importance || 5) * 10 + 20) * this.spacingMultiplier),
      );

    this.renderLinks();
    this.renderNodes();

    // Update positions on simulation tick
    this.simulation.on("tick", () => {
      this.updatePositions();
    });
  }

  renderLinks() {
    // Create a map of node IDs to their importance values
    const nodeImportance = new Map();
    this.nodes.forEach(node => {
      nodeImportance.set(node.id, node.importance || 5);
    });

    // Helper function to calculate edge width based on strength and node importance
    const getEdgeWidth = (edge, nodeId) => {
      const strength = edge.strength || 5;
      const importance = nodeImportance.get(nodeId) || 5;
      // More pronounced scaling: min 2px, max ~30px for high importance
      return Math.max(2, strength * (0.5 + importance * 0.35));
    };

    // Store width calculation function for use in updatePositions
    this._getEdgeWidth = getEdgeWidth;

    const linkElements = this.linkGroup
      .selectAll(".link")
      .data(this.edges)
      .join("path")
      .attr("class", "link")
      .attr("fill", (d) => this.relationshipColors[d.relationship] || "#999")
      .attr("fill-opacity", (d) => d.invisible ? 0 : 0.85)
      .attr("stroke", "none")
      .attr("d", (d) => {
        if (d.invisible) return "";
        // Placeholder, will be updated in updatePositions
        return "M 0 0 L 1 1";
      });

    this.linkElements = linkElements;

    // Render centered arrow heads (skip invisible edges)
    this.renderCenteredArrows();

    // Render edge emojis (if enabled, skip invisible edges)
    this.renderEdgeEmojis();
  }

  renderCenteredArrows() {
    // Create a map of node IDs to their importance values
    const nodeImportance = new Map();
    this.nodes.forEach(node => {
      nodeImportance.set(node.id, node.importance || 5);
    });

    const arrows = this.linkGroup
      .selectAll(".centered-arrow")
      .data(this.edges.filter(d => !d.invisible))  // Filter out invisible edges
      .join("path")
      .attr("class", "centered-arrow")
      .attr("d", (d) => {
        // Scale arrow size based on target node importance and edge strength
        const targetImportance = nodeImportance.get(d.target.id || d.target) || 5;
        const strength = d.strength || 5;
        // Base size 10, scale up to ~30 for important nodes with strong edges
        const arrowSize = 10 + (targetImportance * strength * 0.3);

        return `M 0 -${arrowSize} L ${arrowSize * 1.5} 0 L 0 ${arrowSize} L ${arrowSize * 0.5} 0 Z`;
      })
      .attr("fill", (d) => this.relationshipColors[d.relationship] || "#999")
      .attr("opacity", 0.85)
      .style("pointer-events", "none");

    this.arrowElements = arrows;
  }

  renderEdgeEmojis() {
    if (this.showEdgeEmojis) {
      // Filter out invisible edges
      const visibleEdges = this.edges.filter(d => !d.invisible);

      // Add background circles for emoji labels
      const emojiBackgrounds = this.linkGroup
        .selectAll(".edge-emoji-bg")
        .data(visibleEdges)
        .join("circle")
        .attr("class", "edge-emoji-bg")
        .attr("r", 14)
        .attr("cx", (d) => (d.source.x + d.target.x) / 2)
        .attr("cy", (d) => (d.source.y + d.target.y) / 2)
        .attr("fill", "#1a1a2e")
        .attr(
          "stroke",
          (d) => this.relationshipColors[d.relationship] || "#999",
        )
        .attr("stroke-width", 2)
        .style("pointer-events", "none");

      this.edgeEmojiBackgrounds = emojiBackgrounds;

      // Add emoji text on top
      const edgeEmojis = this.linkGroup
        .selectAll(".edge-emoji")
        .data(visibleEdges)
        .join("text")
        .attr("class", "edge-emoji")
        .attr("text-anchor", "middle")
        .attr("dy", "0.35em")
        .attr("x", (d) => (d.source.x + d.target.x) / 2)
        .attr("y", (d) => (d.source.y + d.target.y) / 2)
        .style("font-size", "16px")
        .style("pointer-events", "none")
        .style("user-select", "none")
        .style("fill", (d) =>
          d.relationship === "leads_to" ? "#ffffff" : "currentColor",
        )
        .style("font-weight", (d) =>
          d.relationship === "leads_to" ? "bold" : "normal",
        )
        .text((d) => this.relationshipEmojis[d.relationship] || "🔗");

      this.edgeEmojiElements = edgeEmojis;
    } else {
      // Remove edge emojis and backgrounds if disabled
      this.linkGroup.selectAll(".edge-emoji").remove();
      this.linkGroup.selectAll(".edge-emoji-bg").remove();
      this.edgeEmojiElements = null;
      this.edgeEmojiBackgrounds = null;
    }
  }

  toggleEdgeEmojis(show) {
    this.showEdgeEmojis = show;
    this.renderEdgeEmojis();
  }

  setSpacing(multiplier) {
    this.spacingMultiplier = multiplier;

    // Update existing simulation forces in real-time
    if (this.simulation) {
      this.simulation
        .force("link")
        .distance((d) => {
          const baseDistance = 150 / (d.strength || 5);
          // For high-strength edges (invisible edges), apply squared multiplier
          const mult = (d.strength || 5) >= 7
            ? Math.pow(this.spacingMultiplier, 2)
            : this.spacingMultiplier;
          return baseDistance * mult;
        })
        .strength((d) => {
          const baseStrength = (d.strength || 5) / 10;
          // For high-strength edges, divide by squared multiplier
          const divisor = (d.strength || 5) >= 7
            ? Math.pow(this.spacingMultiplier, 2)
            : this.spacingMultiplier;
          return baseStrength / divisor;
        });

      this.simulation
        .force("charge")
        .strength(-200 * this.spacingMultiplier);

      this.simulation
        .force("collision")
        .radius((d) => ((d.importance || 5) * 10 + 20) * this.spacingMultiplier);

      // Reheat the simulation to apply changes
      this.simulation.alpha(0.3).restart();
    }
  }

  renderNodes() {
    const nodeElements = this.nodeGroup
      .selectAll(".node")
      .data(this.nodes)
      .join("g")
      .attr("class", (d) => (d.hasChildren ? "node has-children" : "node"))
      .call(this.drag());

    // Clear existing children to prevent duplicates on re-render
    nodeElements.selectAll("*").remove();

    // Background circle
    nodeElements
      .append("circle")
      .attr("class", "node-bg")
      .attr("r", (d) => (d.importance || 5) * 5 + 15)
      .attr("fill", (d, i) => this.getCategoryColor(d.category, i))
      .attr("opacity", 1.0);

    // Outer ring for nodes with children - more prominent
    nodeElements
      .filter((d) => d.hasChildren)
      .append("circle")
      .attr("class", "children-indicator")
      .attr("r", (d) => (d.importance || 5) * 5 + 25)
      .attr("fill", "none")
      .attr("stroke", "#ffffff")  // White for visibility
      .attr("stroke-width", 5)
      .attr("stroke-dasharray", "8,4")  // Longer dashes
      .attr("opacity", 0.9);

    // Emoji
    nodeElements
      .append("text")
      .attr("class", "emoji")
      .attr("text-anchor", "middle")
      .attr("dy", "0.35em")
      .style("font-size", (d) => (d.importance || 5) * 3 + 20 + "px")
      .text((d) => d.emoji || "📌");

    // Label
    nodeElements
      .append("text")
      .attr("class", "label")
      .attr("text-anchor", "middle")
      .attr("dy", (d) => (d.importance || 5) * 5 + 35)
      .text((d) => d.label);

    // Single-click handler (for details)
    nodeElements.on("click", (event, d) => {
      event.stopPropagation();
      this.setSelectedNode(d);
      if (this.onNodeClick) {
        this.onNodeClick(d);
      }
    });

    // Double-click handler (for drill-down)
    nodeElements.on("dblclick", (event, d) => {
      event.stopPropagation();
      if (d.hasChildren && this.onNodeDrillDown) {
        this.onNodeDrillDown(d);
      }
    });

    this.nodeElements = nodeElements;
  }

  updatePositions() {
    this.linkElements
      .attr("d", (d) => {
        if (d.invisible) return "";

        // Calculate widths at each end based on node importance
        const sourceWidth = this._getEdgeWidth ? this._getEdgeWidth(d, d.source.id || d.source) : 3;
        const targetWidth = this._getEdgeWidth ? this._getEdgeWidth(d, d.target.id || d.target) : 3;

        // Calculate the angle of the edge
        const dx = d.target.x - d.source.x;
        const dy = d.target.y - d.source.y;
        const angle = Math.atan2(dy, dx);

        // Calculate perpendicular offset for edge width
        const perpAngle = angle + Math.PI / 2;

        // Create a tapered path with rounded ends
        // Points at source (with rounded cap)
        const sourceOffsetX = Math.cos(perpAngle) * (sourceWidth / 2);
        const sourceOffsetY = Math.sin(perpAngle) * (sourceWidth / 2);
        const sx1 = d.source.x + sourceOffsetX;
        const sy1 = d.source.y + sourceOffsetY;
        const sx2 = d.source.x - sourceOffsetX;
        const sy2 = d.source.y - sourceOffsetY;

        // Points at target (with rounded cap)
        const targetOffsetX = Math.cos(perpAngle) * (targetWidth / 2);
        const targetOffsetY = Math.sin(perpAngle) * (targetWidth / 2);
        const tx1 = d.target.x + targetOffsetX;
        const ty1 = d.target.y + targetOffsetY;
        const tx2 = d.target.x - targetOffsetX;
        const ty2 = d.target.y - targetOffsetY;

        // Simple tapered polygon with square ends
        // Node opacity will hide the endpoints
        return `
          M ${sx1},${sy1}
          L ${tx1},${ty1}
          L ${tx2},${ty2}
          L ${sx2},${sy2}
          Z
        `.replace(/\s+/g, ' ').trim();
      });

    // Update centered arrow positions and rotations
    if (this.arrowElements) {
      this.arrowElements.attr("transform", (d) => {
        const midX = (d.source.x + d.target.x) / 2;
        const midY = (d.source.y + d.target.y) / 2;
        const angle =
          (Math.atan2(d.target.y - d.source.y, d.target.x - d.source.x) * 180) /
          Math.PI;
        return `translate(${midX},${midY}) rotate(${angle})`;
      });
    }

    // Update edge emoji background positions
    if (this.edgeEmojiBackgrounds) {
      this.edgeEmojiBackgrounds
        .attr("cx", (d) => (d.source.x + d.target.x) / 2)
        .attr("cy", (d) => (d.source.y + d.target.y) / 2);
    }

    // Update edge emoji positions (at midpoint)
    if (this.edgeEmojiElements) {
      this.edgeEmojiElements
        .attr("x", (d) => (d.source.x + d.target.x) / 2)
        .attr("y", (d) => (d.source.y + d.target.y) / 2);
    }

    this.nodeElements.attr("transform", (d) => `translate(${d.x},${d.y})`);
  }

  setSelectedNode(node) {
    // Store selected node ID
    this.selectedNodeId = node ? node.id : null;

    // Remove 'selected' class from all nodes
    this.nodeElements.classed("selected", false);

    // Add 'selected' class to the clicked node
    if (node) {
      this.nodeElements.filter((d) => d.id === node.id).classed("selected", true);
    }
  }

  drag() {
    return d3
      .drag()
      .on("start", (event, d) => {
        if (!event.active) this.simulation.alphaTarget(0.3).restart();
        d.fx = d.x;
        d.fy = d.y;
      })
      .on("drag", (event, d) => {
        d.fx = event.x;
        d.fy = event.y;
      })
      .on("end", (event, d) => {
        if (!event.active) this.simulation.alphaTarget(0);
        d.fx = null;
        d.fy = null;
      });
  }

  getCategoryColor(category, index) {
    const colors = [
      "#4A90E2",
      "#7B68EE",
      "#50C878",
      "#FF6B6B",
      "#FFA07A",
      "#20B2AA",
      "#DDA0DD",
      "#F0E68C",
    ];
    // Simple hash function for consistent colors per category
    const hash = category
      ? category.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0)
      : index;
    return colors[hash % colors.length];
  }

  resetView() {
    // Reset zoom/pan
    this.svg
      .transition()
      .duration(750)
      .call(d3.zoom().transform, d3.zoomIdentity);

    // Re-layout: restart the force simulation with updated center
    if (this.simulation) {
      // Update force center to current dimensions
      this.simulation.force('center', d3.forceCenter(this.width / 2, this.height / 2));

      // Reheat the simulation to re-layout nodes
      this.simulation.alpha(1).restart();
    }
  }

  clear() {
    if (this.simulation) {
      this.simulation.stop();
    }
    this.linkGroup.selectAll("*").remove();
    this.nodeGroup.selectAll("*").remove();
    this.nodes = [];
    this.edges = [];
  }
}
